import React, { useState } from 'react';
import { ChevronDown, ChevronRight, ArrowDown } from 'lucide-react';

// THE DAILY HABITS - A STAGING AREA, NOT A CHECKLIST.
//
// Greg, 2026-09-11: "I'd like for daily to actually eliminate these. I need to be able to
// schedule them. Like, if I'm going to the gym, I may be going to gym, wanna stretch, wanna
// drink two waters. And then when I get home, I'm gonna shower... So clicking them from daily
// shouldn't scratch them off. Clicking them should drop them down into up next and take them
// away... I wanna clear all of these out, get them into up next, and scratch them off. That
// would be motivational."
//
// ⚠️ CLICKING SCHEDULES, IT DOES NOT COMPLETE. That is the whole inversion. A habit is done at a
// point in the day, not whenever you remember the list exists, so the strip is where the day
// gets built and Up Next is where it gets worked. Ticking here would let him "finish" the gym
// while sitting at his desk, which is how a habit list becomes decoration.
//
// ⚠️ THE COUNT RUNS DOWN, NOT UP. It read 0/23 and he wanted 23 of 23 falling to zero: "my goal
// was to get it to zero." Progress you can see is the point - a counter climbing away from zero
// measures the same thing and motivates nobody.
//
// ⚠️ HABITS STILL NEVER JOIN THE TODAY TASK POOL. While they did, a glass of water carried the
// visual weight of a $169,000 pricing decision and "23 things left today" was mostly hydration.

// 10k steps is written by the step sync, never by a tap, so it is not schedulable and renders
// visibly inert instead of as a control that silently does nothing.
const isAuto = label => String(label || '').toLowerCase().includes('10k steps');

export default function DailyStrip({ items, scheduledIds = [], onSchedule }) {
  const [open, setOpen] = useState(false);
  if (!items || !items.length) return null;

  const sched = new Set((scheduledIds || []).map(String));
  // What is LEFT to place: not already scheduled into today's order, and not already ticked off
  // (a habit he completed straight from Up Next must not reappear here asking to be scheduled).
  const left = items.filter(i => !sched.has(String(i.id)) && !i.completed);
  const total = items.length;
  const cleared = total - left.length;
  const allPlaced = left.length === 0;

  return (
    <div style={{
      border: '1px solid ' + (allPlaced ? '#b7e1c4' : 'var(--gray-200)'),
      background: allPlaced ? '#f3fbf5' : '#fcfcfd',
      borderRadius: 8, padding: '8px 12px', marginBottom: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={() => setOpen(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>
          {open ? <ChevronDown size={13} color="var(--gray-500)" /> : <ChevronRight size={13} color="var(--gray-500)" />}
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-600)', letterSpacing: 0.4 }}>DAILY</span>
        </button>

        {!open && !allPlaced && (
          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
            {left.map(i => (
              <span key={i.id} title={i.text + (isAuto(i.text) ? '' : ' — click to drop it into Up Next')}
                onClick={() => !isAuto(i.text) && onSchedule && onSchedule(i)}
                style={{
                  width: 11, height: 11, borderRadius: '50%',
                  background: 'transparent', border: '1.5px solid var(--gray-300)',
                  cursor: isAuto(i.text) ? 'default' : 'pointer', flexShrink: 0,
                }} />
            ))}
          </div>
        )}
        {(open || allPlaced) && <div style={{ flex: 1 }} />}

        {allPlaced ? (
          <span style={{ fontSize: 11.5, fontWeight: 700, color: '#276749' }}>
            All {total} placed &mdash; work them in Up Next
          </span>
        ) : (
          <span style={{ fontSize: 11, fontWeight: 700, color: cleared > 0 ? 'var(--orange)' : 'var(--gray-500)', flexShrink: 0 }}>
            {left.length} of {total} left
          </span>
        )}
      </div>

      {open && !allPlaced && (
        <>
          <div style={{ fontSize: 10.5, color: 'var(--gray-500)', margin: '8px 0 6px' }}>
            Click one to drop it into Up Next at the point in the day you will do it. Scratch it
            off there, not here.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {left.map(i => {
              const auto = isAuto(i.text);
              return (
                <button key={i.id} disabled={auto} onClick={() => onSchedule && onSchedule(i)}
                  title={auto ? 'Recorded automatically from your step count' : 'Drop into Up Next'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '4px 9px', borderRadius: 14, fontSize: 11.5, fontFamily: 'inherit',
                    background: '#fff', border: '1px solid var(--gray-200)', color: 'var(--gray-700)',
                    cursor: auto ? 'default' : 'pointer', opacity: auto ? 0.55 : 1,
                  }}>
                  {!auto && <ArrowDown size={10} color="var(--orange)" />}
                  {i.text}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
